import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import useStyles from 'isomorphic-style-loader/useStyles';
import {
	AuthService,
	Container,
	i18n,
	ILearningExperience,
	EnrollmentStatus,
} from '../../../../symphony';

import { CategorizedCourses } from '../../containers/CategorizedCourses/CategorizedCourses';
import { CoursesSlider } from '../CoursesSlider/CoursesSlider';
import { AddCourse } from '../AddCourse/AddCourse';
import { ProgrammingLanguages } from '../ProgrammingLanguages/ProgrammingLanguages';

import s from './CoursesCatalog.scss';
import { ICategorizedCourse } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';

interface IFilteredCourses {
	enrolled: ILearningExperience[];
	unEnrolled: ILearningExperience[];
}

interface ICoursesProps {
	courses: ILearningExperience[];
	categories: ICategorizedCourse[];
}

export function CoursesCatalog({
	courses,
	categories,
}: ICoursesProps): JSX.Element {
	useStyles(s);
	const isLoggedIn = Container.take('global', AuthService).isLoggedIn();
	const [searchParams] = useSearchParams();
	const type = searchParams.get('type');

	const filteredCourses: IFilteredCourses = useMemo(() => {
		const newCourses = courses.filter(
			(course) => course.enrollmentStatusId === EnrollmentStatus.None
                || course.enrollmentStatusId === EnrollmentStatus.Removed
                || course.enrollmentStatusId === EnrollmentStatus.EnrolledFromPath,
		);
		const enrolledCourses = courses.filter(
			(course) => course.enrollmentStatusId === EnrollmentStatus.Enrolled,
		);

		return {
			unEnrolled: newCourses,
			enrolled: enrolledCourses,
		};
	}, []);

	const deprecationNotification = type === 'replaced' && (
		<p className="le-courses-catalog__warning">
			The course you are looking for has been deprecated. Please choose
			one of our up-to-date courses from the catalog.
		</p>
	);

	const renderHeaderSection = () => {
		if (isLoggedIn) {
			return (
				<>
					{deprecationNotification}
					<h2 className="le-courses-catalog__title">
						{i18n.t('web-le.my-courses-catalog-title')}
					</h2>
					{filteredCourses.enrolled?.length ? (
						<CoursesSlider
							courses={filteredCourses.enrolled.sort(
								(a, b) => a.progressionStatusId
                                    - b.progressionStatusId,
							)}
						/>
					) : (
						<AddCourse />
					)}
				</>
			);
		}
		return (
			<>
				{deprecationNotification}
				<h2 className="le-courses-catalog__title">
					{i18n.t('web-le.top-courses-catalog-title')}
				</h2>
				<CoursesSlider
					courses={courses.filter((el) => el.uiConfigurations?.isTop)}
				/>
			</>
		);
	};

	return filteredCourses ? (
		<main className="le-courses-catalog" sl-test-data="cmpCoursesCatalog">
			<section className="le-courses-catalog__header">
				{renderHeaderSection()}
			</section>
			<div
				className={`le-courses-catalog__main${
					isLoggedIn ? '' : ' non-logged'
				}`}
			>
				{!isLoggedIn && <ProgrammingLanguages />}
				<CategorizedCourses
					categories={categories}
					courses={filteredCourses.unEnrolled}
				/>
			</div>
		</main>
	) : null;
}
